// US 提现相关请求工具。
// 入参沿用 public/utils/request.js 的语义（JSON 字符串），但失败返回值不再是
// "exception=..." 字符串：调用方全都按对象用（res.code / res.data / res.msg），
// 返回字符串会导致「toast 出 undefined」「列表被赋成字符串后按字符渲染」
// 「校验请求失败却当成校验通过继续放行」这类问题。现在统一返回带 __failed 标记的对象。
import axios from 'axios'
import store from '@/store'
import { baseUrl, reportBuriedPointUrl, handleTokenExpired } from '@/utils/http'

const FAIL_MSG = 'Network error. Please try again.'

// 请求是否失败。/check、listAccount 这类接口直接返回裸数据、没有 code 字段，
// 不能用 res.code 判断，一律走这个。
export function isRequestFailed (res) {
  return !res || res.__failed === true
}

// token 过期：http.js 的拦截器只挂在它自己的 axios 实例上，这里用的是裸 axios，
// 所以得自己调一次 handleTokenExpired，否则提现页的 401 完全没人处理。
function toFailure (error) {
  if (error && error.response && error.response.status === 401) {
    handleTokenExpired()
  }
  return { code: -1, msg: FAIL_MSG, __failed: true }
}

// HTTP 200 但业务码 401 是另一种过期形态，和 http.js 的拦截器保持一致
function unwrap (resp) {
  const data = resp.data
  if (data && data.code === 401) {
    handleTokenExpired()
    return { code: 401, msg: FAIL_MSG, __failed: true }
  }
  return data
}

// 登录态 token：优先取内存里的 vuex（与 http.js 的请求拦截器同源），
// 取不到时回退到 localStorage 里持久化的那份
export function getToken () {
  if (store.state.token) {
    return store.state.token
  }
  try {
    const saved = JSON.parse(localStorage.getItem('vuex'))
    return (saved && saved.token) || ''
  } catch (e) {
    return ''
  }
}

function authHeaders (extra) {
  const headers = Object.assign({
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }, extra || {})
  const token = getToken()
  if (token) {
    headers.token = token
  }
  return headers
}

// data 传入的是 JSON 字符串，保持与原 request.js 一致
export function postRequest (url, data) {
  return axios({
    method: 'post',
    url,
    data: data == null ? undefined : JSON.stringify(JSON.parse(data)),
    baseURL: baseUrl,
    headers: authHeaders()
  })
    .then(unwrap)
    .catch(toFailure)
}

export function formPostRequest (url, formData) {
  return axios({
    method: 'post',
    url,
    data: formData,
    baseURL: baseUrl,
    headers: authHeaders({ 'Content-Type': 'multipart/form-data' })
  })
    .then(unwrap)
    .catch(toFailure)
}

export function getRequest (url, params) {
  return axios({
    method: 'get',
    url,
    params,
    baseURL: baseUrl,
    headers: authHeaders()
  })
    .then(unwrap)
    .catch(toFailure)
}

export function reportBuriedPointRequest (url, data) {
  return axios({
    method: 'post',
    url,
    data: JSON.stringify(JSON.parse(data)),
    baseURL: reportBuriedPointUrl,
    headers: authHeaders()
  })
    .then(unwrap)
    .catch(toFailure)
}

// 轻量 toast，复刻 request.js 里的 toast()（不旋转，居中偏下）。
// 不用 src/utils/toast，因为那个会针对竖屏做 rotate(90deg)，而 US 提现页本身就是竖屏布局，不需要旋转。
export function toast (msg, duration) {
  duration = isNaN(duration) ? 3000 : duration
  const m = document.createElement('div')
  m.innerHTML = msg
  m.style.cssText = `
    display: inline-block;
    max-width: 80%;
    padding: 0.14rem 0.24rem;
    background: rgba(0, 0, 0, 0.8);
    font-size: 0.26rem;
    color: #fff;
    line-height: 1.4;
    text-align: center;
    border-radius: 0.12rem;
    position: fixed;
    bottom: 30%;
    left: 50%;
    transform: translateX(-50%);
    z-index: 999999;
    word-break: break-word;
  `
  document.body.appendChild(m)
  setTimeout(() => {
    const d = 0.5
    m.style.transition = `transform ${d}s ease-in, opacity ${d}s ease-in`
    m.style.opacity = '0'
    setTimeout(() => {
      if (m.parentNode) {
        document.body.removeChild(m)
      }
    }, d * 1000)
  }, duration)
}

export default {
  postRequest,
  formPostRequest,
  getRequest,
  reportBuriedPointRequest,
  isRequestFailed,
  toast
}
